import { useCallback } from 'react';

import { Practitioner } from '@dreryk/egabinet-private-medical-service-frontend-api';

import {
   InteractiveTable,
   InteractiveTableSelectorItemFilter,
   InteractiveTableSelectorItemKeyExtractor,
   InteractiveTableSelectorItemRenderer,
   InteractiveTableSelectorMapper,
   InteractiveTableSelectorTableKeyExtractor,
   Text,
} from '@dreryk/react-components';

import { SpecialtiesText } from '../../../../../../shared/specialties/SpecialtiesText';
import { receptionServiceTranslationKeys } from '../../../../../../translations';
import { ReceptionServiceFormBean, ReceptionServiceFormController } from '../../../ReceptionServiceForm';
import { ReceptionServicePractitionerValue } from '../receptionServicePractitioner.types';
import { mapPractitionerToReceptionServicePractitioner } from '../receptionServicePractitioner.utils';

const t = receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.selector;

type ReceptionServicePractitionersInteractiveTableSelectorProps = Pick<
   ReceptionServiceFormController,
   'register' | 'control'
> & {
   readonly practitioners: Practitioner[];
};

// eslint-disable-next-line max-lines-per-function
export const ReceptionServicePractitionersInteractiveTableSelector = (
   props: ReceptionServicePractitionersInteractiveTableSelectorProps
): JSX.Element => {
   const mapper = useCallback<InteractiveTableSelectorMapper<Practitioner, ReceptionServicePractitionerValue>>(
      (practitioner) => {
         const deps: ReceptionServiceFormBean = props.control.getValues();
         return mapPractitionerToReceptionServicePractitioner(deps)(practitioner);
      },
      [props.control]
   );

   const itemRenderer = useCallback<InteractiveTableSelectorItemRenderer<Practitioner>>((practitioner, Highlight) => {
      return (
         <span>
            <Highlight
               node={
                  <Text testId={'fullName'}>
                     {practitioner.firstName.asString()} {practitioner.lastName.asString()}
                  </Text>
               }
            />
            {', '}
            <Highlight node={<SpecialtiesText specialties={practitioner.specialtyNames} secondary />} />
         </span>
      );
   }, []);

   const itemKeyExtractor = useCallback<InteractiveTableSelectorItemKeyExtractor<Practitioner>>((practitioner) => {
      return practitioner.id.toString();
   }, []);

   const tableKeyExtractor = useCallback<InteractiveTableSelectorTableKeyExtractor<ReceptionServicePractitionerValue>>(
      (value) => {
         return value.id.toString();
      },
      []
   );

   const itemFilter = useCallback<InteractiveTableSelectorItemFilter<Practitioner>>((item) => {
      return [item.firstName.asString(), item.lastName.asString(), ...item.specialtyNames];
   }, []);

   return (
      <InteractiveTable.Selector<Practitioner, ReceptionServicePractitionerValue, ReceptionServiceFormBean>
         register={props.register}
         testId={'practitionersSelector'}
         name={'practitioners.selector'}
         label={t.label}
         placeholder={t.placeholder}
         items={props.practitioners}
         itemKeyExtractor={itemKeyExtractor}
         tableKeyExtractor={tableKeyExtractor}
         itemRenderer={itemRenderer}
         itemFilter={itemFilter}
         mapper={mapper}
         required
      />
   );
};
